import { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import CDF from '../../components/Sections/CDF';

const validationSchema = Yup.object({
  'years-employed': Yup.string().required('Required'),
  'what-is-your-objective': Yup.string().required('Required'),
  'case-involvement': Yup.string().required('Required'),
  'last-day-or-incident': Yup.string().required('Required'),
});

const TIME_LIMIT_EXTENSION_DATE = new Date('2026-10-01');

const NoWinNoFeeCalculator = ({ classNames = '' }) => {
  const [formSubmittedSuccessfully, setFormSubmittedSuccessfully] = useState(null);
  const [eligibilityStatus, setEligibilityStatus] = useState(null);

  const checkEligibility = (values) => {
    const {
      'years-employed': yearsEmployed,
      'what-is-your-objective': objective,
      'case-involvement': caseInvolvement,
      'last-day-or-incident': lastDayOrIncident,
    } = values;

    const lastIncidentDate = new Date(lastDayOrIncident);
    const currentDate = new Date();

    // Calculate the difference in months between the current date and the last incident date
    const monthsDifference =
      (currentDate.getFullYear() - lastIncidentDate.getFullYear()) * 12 +
      (currentDate.getMonth() - lastIncidentDate.getMonth());

    const monthsAllowed = lastIncidentDate >= TIME_LIMIT_EXTENSION_DATE ? 6 : 3;

    if (
      yearsEmployed === 'More than 2 years' &&
      caseInvolvement !== 'None' &&
      objective !== 'No' &&
      monthsDifference <= monthsAllowed
    ) {
      return true;
    }
    return false;
  };

  const handleFormSubmit = async (values) => {
    try {
      const eligible = checkEligibility(values);
      setEligibilityStatus(eligible);
      setFormSubmittedSuccessfully(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormSubmittedSuccessfully(false);
    }
  };

  return (
    <div>
      {!formSubmittedSuccessfully && (
        <div className={`cdf cal ${classNames}`}>
          <h5 className="status">Just answer the following easy questions</h5>
          <p className="status-disc">
            We’ll quickly let you know if we can help you with No Win No Fee terms
          </p>
          <Formik
            initialValues={{
              'years-employed': '',
              'what-is-your-objective': '',
              'case-involvement': '',
              'last-day-or-incident': '',
            }}
            onSubmit={handleFormSubmit}
            validationSchema={validationSchema}
          >
            {({ errors, touched, isValid, isSubmitting }) => (
              <Form className="cdf__form">
                <div className="cdf__form__fields">
                  <div className="cdf__field__wrapper">
                    <p>
                      Years employed <span>*</span>
                    </p>
                    <Field as="select" name="years-employed">
                      <option value="" defaultValue="selected">
                        - Select -
                      </option>
                      <option value="Less than 2 years">Less than 2 years</option>
                      <option value="More than 2 years">More than 2 years</option>
                    </Field>
                    {errors['years-employed'] && touched['years-employed'] ? (
                      <i className="cdf__field__error">{errors['years-employed']}</i>
                    ) : null}
                  </div>

                  <div className="cdf__field__wrapper">
                    <p>
                      Want to leave job? <span>*</span>
                    </p>
                    <Field as="select" name="what-is-your-objective">
                      <option value="" defaultValue="selected">
                        - Select -
                      </option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                      <option value="Maybe">Maybe</option>
                      <option value="Already left">Already left</option>
                    </Field>
                    {errors['what-is-your-objective'] && touched['what-is-your-objective'] ? (
                      <i className="cdf__field__error">{errors['what-is-your-objective']}</i>
                    ) : null}
                  </div>

                  <div className="cdf__field__wrapper">
                    <p>
                      Does your case involve any of these? <span>*</span>
                    </p>
                    <Field as="select" name="case-involvement">
                      <option value="" defaultValue="selected">
                        - Select -
                      </option>
                      <option value="None">None</option>
                      <option value="Discrimination">Discrimination</option>
                      <option value="Health & Safety">Health & Safety</option>
                      <option value="Whistleblowing">Whistleblowing</option>
                    </Field>
                    {errors['case-involvement'] && touched['case-involvement'] ? (
                      <i className="cdf__field__error">{errors['case-involvement']}</i>
                    ) : null}
                  </div>

                  <div className="cdf__field__wrapper">
                    <p>
                      Last day of work or last incident <span>*</span>
                    </p>
                    <Field type="date" name="last-day-or-incident" />
                    {errors['last-day-or-incident'] && touched['last-day-or-incident'] ? (
                      <i className="cdf__field__error">{errors['last-day-or-incident']}</i>
                    ) : null}
                  </div>
                </div>

                <button disabled={!isValid || isSubmitting} className="button" type="submit">
                  {isSubmitting ? 'SUBMITTING' : 'SUBMIT'}
                </button>
                <p className="cdf__field__footer-text">Completely confidential</p>
                {formSubmittedSuccessfully === false && (
                  <p className="cdf__field__footer-text">
                    <strong>Error submitting the form</strong>
                  </p>
                )}
              </Form>
            )}
          </Formik>
        </div>
      )}
      {eligibilityStatus === true && (
        <div className="after-submit">
          <h5 className="status">Thanks for answering those questions!</h5>
          <p className="status-disc">It looks like you may be eligible for No Win No Fee.</p>
          <p className="status-disc">
            So that we can confirm your eligibility, please complete and submit this form. Our aim
            is to confirm your eligibility on the same day.
          </p>
          <CDF />
        </div>
      )}
      {eligibilityStatus === false && (
        <div className="after-submit cdf">
          <h5 className="status">Thanks for answering those questions!</h5>
          <p className="status-disc">
            We need some more information to assess your eligibility for No Win No Fee, so...
          </p>
          <p className="status-disc">
            Please complete and submit this form, so we can review your case further. Our aim is to
            confirm your eligibility on the same day if possible.
          </p>
          <CDF />
        </div>
      )}
    </div>
  );
};

export default NoWinNoFeeCalculator;
